/* ============================================================================
   A2 Bikes — Launch page scene engine (a2-launch.js). Pairs with a2-launch.css.
   Ported from docs/mockups/sp-drop2-motion.html. ES5, no libraries, one rAF
   loop (read all layout first, then write). Safe with several instances of
   any contract, in any order, with any subset of sections present.
   Only global: window.A2Launch = { refresh } (also re-run automatically on
   Shopify theme-editor section load/unload/reorder; shopify:block:select
   scrolls the selected block into view, best effort).
   The loop idles: it stops after ~1 s with no scroll/resize/layout change, no
   visible running marquee and no running counter, and restarts on scroll,
   resize, load, fonts.ready, pointer/key input, IntersectionObserver changes
   and refresh(). Clocks tick on their own 1 s interval.

   FONTS: this file and a2-launch.css do NOT load fonts. The nav section
   (a2l-nav) must emit preconnect + <link rel="stylesheet"> for Google Fonts
   Inter 400-800 + Barlow Condensed 600-800 (display=swap). The engine
   re-measures (marquee wrap width, track widths) on document.fonts.ready.

   REQUIRED INLINE SNIPPET (avoids a flash of the static layout before this
   deferred file runs). The a2l-nav section must emit, verbatim, at the very
   top of its markup:
     <script>document.documentElement.classList.add('a2l-js'); if (matchMedia('(prefers-reduced-motion: reduce)').matches) document.documentElement.classList.add('a2l-static'); (function (h, w) { function f() { if (!w.A2Launch) { h.classList.remove('a2l-js'); } } w.addEventListener('load', function () { w.setTimeout(f, 1500); }); w.setTimeout(f, 10000); })(document.documentElement, window);</script>
   The tail is a safety net: if this engine has not defined window.A2Launch
   1.5 s after window load (or 10 s after the snippet ran, should load never
   fire), it removes a2l-js so the page falls back to the no-JS end states
   instead of leaving content pinned at --p:0 / hidden .a2l-rv blocks.
   This script tolerates both classes already being set (idempotent), re-adds
   a2l-js if it runs after the safety net fired, and still follows later
   changes of the reduced-motion preference.

   NO-JS: without this script (no html.a2l-js) the page renders like the
   static mode: scenes unpinned, end states visible, nothing left at opacity 0.

   DOM CONTRACTS
   -------------
   <html> (set by this script)
     .a2l-js              script is running (enables the .a2l-rv hidden state)
     .a2l-static          prefers-reduced-motion: scenes unpinned, end states
     --a2l-top            px bottom edge of the theme's sticky announcement bar
                          + header stack (measured every frame, 0 when hidden).
                          Only visible (display/visibility/opacity), outermost
                          sticky/fixed header elements count, stacked
                          contiguously from the viewport top, each < 50% vh.
     --a2l-nav            px height of the local nav ([data-a2l-nav])
     --a2l-pin-h          px height available to a pinned scene: the measured
                          height of a scene's .a2l-pin (100svh - top - nav), or
                          innerHeight - top - nav when nothing is pinned. CSS
                          sizes pinned content from it (lineup image, colours
                          frame, statement and OCT type) so every scene fits.

   [data-a2l-nav]         The local nav bar (.a2l-lnav). Its parent element is
                          used as a placeholder (min-height reserved); the bar
                          gets .stuck (position:fixed at --a2l-top) once the
                          placeholder scrolls under the theme header.

   .a2l-progress          Gets --page (0..1 page scroll). Child <i> is the bar.

   [data-a2l-scene="hero|statement|lineup|reveal|wheels|colours|oct|steps"]
                          Gets --p (0..1, 4dp). Pinned scenes (everything but
                          "steps") contain a direct child .a2l-pin (sticky);
                          p = (pinTop - rect.top) / (offsetHeight - pinHeight)
                          with pinTop = --a2l-top + --a2l-nav.
                          "steps": p = (0.75*vh - rect.top) / rect.height.
                          Static end state: hero --p:0 (CSS shows the date),
                          all others --p:1.

   [data-a2l-words]       Text is split into <span class="a2l-word"> (inline
     data-a2l-hot="a,b"   markup kept). Words whose letters/digits match an
                          entry (case-insensitive, punctuation ignored) also
                          get .hot. Words get .lit progressively from the
                          closest ancestor-or-self scene's p (floor(p*1.15*n));
                          with no scene, or in static mode, all are lit.

   [data-a2l-count="120"] Counts 0 -> value (ease-out cubic, 1.1 s) when its
                          .a2l-rv ancestor enters view. MUST sit inside an
                          .a2l-rv (that hides the jump to 0; a loose counter
                          still works but is visible while it resets).
                          Decimals follow the attribute ("4.5" -> 1 dp).
                          Markup holds the final value (no-JS / static).
                          .a2l-stat: the number is <b class="a2l-num"> and the
                          label <span> must be a DIRECT child of .a2l-stat
                          (styled by .a2l-stat > span).

   .a2l-rv (+ .a2l-d1..d3) Gets .on when it enters view (IntersectionObserver,
                          bottom margin -10%). Visible at rest without JS.

   [data-a2l-marquee]     The moving row (.a2l-row). Its children are cloned
     ="reverse"?          once (clones get data-a2l-clone + aria-hidden).
                          Drifts 1px/frame (60fps-normalised) plus scroll
                          velocity; scrolling up reverses it. "reverse" flips
                          the base direction. Transform is only written while
                          the row is in view (IntersectionObserver); drift
                          position is kept. Pauses while the strip (the
                          row's parent) is hovered or has focus inside.
                          Static: no drift, clones hidden.

   [data-a2l-speed="-0.12"] Parallax: gets --v = (centreY - vh/2) * speed (px).

   [data-a2l-track]       Inside a lineup scene: translateX(-(scrollWidth -
                          viewportWidth) * p). Each child gets --in (0..1) as
                          it enters from the right. Static: wraps, no motion.
                          Keyboard: focus inside a card resets the pin's
                          scrollLeft (the browser's focus scroll) and scrolls
                          the window to the progress that centres that card.

   [data-a2l-colours]     Colour switcher (usually the colours scene itself).
     data-tints='["#e3ebf7","#eceeef"]'   pin tints by index (or per-button
                          data-tint). Children:
       img[data-i="0"]    finish images; the active one gets .on, the
                          others aria-hidden="true"
       button[data-i="0"] swatches; aria-pressed kept in sync; clickable.
                          Name = data-name || aria-label; sub-line = data-sub
       [data-a2l-colour-label]  rewritten to "Name<span>sub</span>"
                          Index follows scroll: min(n-1, floor(p*n)) of the
                          closest scene; a click holds until scroll moves the
                          index again. --tint is set on the inner .a2l-pin.

   [data-a2l-steps]       Ordered list; li.lit when p >= i/(n-1) - .02. Its
                          progress comes from the closest ancestor-or-self
                          scene; a list without one is its own "steps" scene.

   [data-a2l-clock][data-target="2026-10-12T09:00:00-07:00"]
                          Live countdown, 1 s tick.
       data-format="nav"  element text becomes "5D 03H 12M"
       [data-u="d|h|m|s"] otherwise each child shows its unit (h/m/s padded);
                          changed digits get .flip (not in static mode)
       data-done="text"   optional nav text once the target has passed; the
                          clock element gets .done at zero either way.
   ========================================================================== */
(function (w, d) {
  'use strict';
  if (w.A2Launch) { return; }

  var root = d.documentElement;
  var raf = w.requestAnimationFrame || function (f) { return w.setTimeout(function () { f(Date.now()); }, 16); };
  var mq = w.matchMedia ? w.matchMedia('(prefers-reduced-motion: reduce)') : null;
  /* The nav section's inline snippet may already have set both classes. */
  var reduce = mq ? !!mq.matches : root.classList.contains('a2l-static');

  root.classList.add('a2l-js');
  root.classList.toggle('a2l-static', reduce);

  var HEADER_SEL = '.shopify-section-group-header-group, .shopify-section-group-header-group > *, ' +
    '.shopify-section-group-header-group > * > *, #shopify-section-header, #shopify-section-announcement-bar, ' +
    '.shopify-section-header, .header-section, header, .header, .site-header, .announcement-bar, sticky-header, ' +
    '[data-section-type="header"], [data-sticky-header]';

  var scenes = [], navs = [], bars = [], marquees = [], paras = [], clocks = [], counters = [], orphanWords = [];
  var headers = [], io = null, mio = null, clockTimer = null;
  var cur = { top: -1, nav: -1, y: -1, vh: -1, vw: -1, page: '' };
  var force = true, needMeasure = true, lastCollect = -1e9, lastT = 0;
  var running = false, kicked = false, lastBusy = 0, pinH = -1;

  /* ---------- helpers ---------- */
  function clamp(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
  function each(list, fn) { for (var i = 0; i < list.length; i++) { fn(list[i], i); } }
  function all(sel, ctx) { return Array.prototype.slice.call((ctx || d).querySelectorAll(sel)); }
  function closestAttr(el, attr) {
    while (el && el.nodeType === 1) { if (el.hasAttribute(attr)) { return el; } el = el.parentNode; }
    return null;
  }
  function insideA2l(el) {
    while (el && el.nodeType === 1) { if (el.classList.contains('a2l')) { return true; } el = el.parentNode; }
    return false;
  }
  function norm(s) { return String(s).toLowerCase().replace(/^[^0-9a-z\u00c0-\u024f]+|[^0-9a-z\u00c0-\u024f]+$/g, ''); }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function setVar(el, name, val) { el.style.setProperty(name, val); }
  /* (Re)start the rAF loop; it idles itself again ~1 s after the last change. */
  function kick() {
    kicked = true;
    if (!running) { running = true; lastT = 0; raf(frame); }
  }
  function sceneFor(el) {
    var s = closestAttr(el, 'data-a2l-scene') || closestAttr(el, 'data-a2l-steps');
    for (var i = 0; i < scenes.length; i++) { if (scenes[i].el === s) { return scenes[i]; } }
    return null;
  }

  /* ---------- word split ---------- */
  function splitWords(el) {
    if (!el.hasAttribute('data-a2l-split')) {
      var hot = {};
      each(String(el.getAttribute('data-a2l-hot') || '').split(','), function (h) { var k = norm(h); if (k) { hot[k] = 1; } });
      var texts = [];
      (function walk(n) {
        for (var c = n.firstChild; c; c = c.nextSibling) {
          if (c.nodeType === 3) { texts.push(c); } else if (c.nodeType === 1 && !/^(SCRIPT|STYLE|BR)$/.test(c.nodeName)) { walk(c); }
        }
      })(el);
      each(texts, function (t) {
        var parts = t.nodeValue.split(/(\s+)/), frag = d.createDocumentFragment();
        each(parts, function (p) {
          if (!p) { return; }
          if (/^\s+$/.test(p)) { frag.appendChild(d.createTextNode(p)); return; }
          var s = d.createElement('span');
          s.className = 'a2l-word' + (hot[norm(p)] ? ' hot' : '');
          s.textContent = p;
          frag.appendChild(s);
        });
        t.parentNode.replaceChild(frag, t);
      });
      el.setAttribute('data-a2l-split', '1');
    }
    return { el: el, words: all('.a2l-word', el), lit: -1 };
  }
  function lightWords(g, n) {
    if (g.lit === n) { return; }
    each(g.words, function (wd, i) { wd.classList.toggle('lit', i < n); });
    g.lit = n;
  }

  /* ---------- colours ---------- */
  function makeColours(el) {
    var prev = el.__a2l;
    var c = { el: el, imgs: [], btns: all('button[data-i]', el), label: el.querySelector('[data-a2l-colour-label]'),
      tints: [], cur: -1, scrollIdx: -1, tintEl: el.querySelector('.a2l-pin') || el };
    each(all('[data-i]', el), function (n) { if (n.nodeName !== 'BUTTON') { c.imgs.push(n); } });
    try { c.tints = JSON.parse(el.getAttribute('data-tints') || '[]') || []; } catch (e) { c.tints = []; }
    c.count = Math.max(c.imgs.length, c.btns.length);
    c.set = function (i) {
      if (i === c.cur || i < 0 || i >= c.count) { return; }
      c.cur = i;
      var name = '', sub = '', tint = c.tints[i] || '';
      each(c.imgs, function (im) {
        var on = Number(im.getAttribute('data-i')) === i;
        im.classList.toggle('on', on);
        if (on) { im.removeAttribute('aria-hidden'); } else { im.setAttribute('aria-hidden', 'true'); }
      });
      each(c.btns, function (b) {
        var on = Number(b.getAttribute('data-i')) === i;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) { name = b.getAttribute('data-name') || b.getAttribute('aria-label') || ''; sub = b.getAttribute('data-sub') || ''; tint = b.getAttribute('data-tint') || tint; }
      });
      if (c.label && name) {
        c.label.textContent = name;
        if (sub) { var s = d.createElement('span'); s.textContent = sub; c.label.appendChild(s); }
      }
      if (tint) { setVar(c.tintEl, '--tint', tint); }
    };
    if (!el.hasAttribute('data-a2l-bound')) {
      el.setAttribute('data-a2l-bound', '1');
      el.addEventListener('click', function (e) {
        var b = e.target;
        while (b && b !== el && b.nodeName !== 'BUTTON') { b = b.parentNode; }
        if (b && b !== el && b.hasAttribute('data-i')) { el.__a2l.set(Number(b.getAttribute('data-i'))); }
      });
    }
    el.__a2l = c;
    if (prev && prev.cur >= 0 && prev.cur < c.count) { c.scrollIdx = prev.scrollIdx; c.set(prev.cur); } else { c.set(0); }
    return c;
  }

  /* ---------- counters ---------- */
  function startCount(el) {
    if (el.__a2lCounted) { return; }
    el.__a2lCounted = true;
    var raw = String(el.getAttribute('data-a2l-count')), to = parseFloat(raw);
    if (isNaN(to)) { return; }
    var dec = raw.indexOf('.') > -1 ? raw.length - raw.indexOf('.') - 1 : 0;
    if (reduce) { el.textContent = to.toFixed(dec); return; }
    counters.push({ el: el, to: to, dec: dec, t0: 0 });
    kick();
  }
  function reveal(el) {
    el.classList.add('on');
    if (el.hasAttribute('data-a2l-count')) { startCount(el); }
    each(all('[data-a2l-count]', el), startCount);
  }

  /* ---------- clocks ---------- */
  function tickClocks() {
    var now = Date.now();
    each(clocks, function (c) {
      var s = Math.max(0, Math.floor((c.target - now) / 1000));
      var v = { d: String(Math.floor(s / 86400)), h: pad(Math.floor(s % 86400 / 3600)), m: pad(Math.floor(s % 3600 / 60)), s: pad(s % 60) };
      c.el.classList.toggle('done', s === 0);
      if (c.el.getAttribute('data-format') === 'nav') {
        var t = s === 0 && c.el.getAttribute('data-done') ? c.el.getAttribute('data-done') : v.d + 'D ' + v.h + 'H ' + v.m + 'M';
        if (c.el.textContent !== t) { c.el.textContent = t; }
        return;
      }
      each(c.units, function (u) {
        var k = u.getAttribute('data-u'), val = v[k];
        if (val === undefined || c.last[k] === val) { return; }
        u.textContent = val;
        if (c.last[k] !== undefined && !reduce) { u.classList.remove('flip'); void u.offsetWidth; u.classList.add('flip'); }
        c.last[k] = val;
      });
    });
  }

  /* ---------- sticky header stack ---------- */
  /* Hidden = display:none, visibility:hidden, or opacity 0 on it or any ancestor. */
  function isHidden(el) {
    var cs = w.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.visibility === 'collapse') { return true; }
    for (var n = el; n && n.nodeType === 1 && n !== d.body; n = n.parentNode) {
      var c = n === el ? cs : w.getComputedStyle(n);
      if (c.display === 'none' || parseFloat(c.opacity) === 0) { return true; }
    }
    return false;
  }
  function contains(a, b) { return a !== b && a.contains(b); }
  function collectHeaders() {
    headers = [];
    each(all(HEADER_SEL), function (el) {
      if (insideA2l(el)) { return; }
      for (var i = 0; i < headers.length; i++) { if (contains(headers[i].el, el)) { return; } }
      var cs = w.getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== '-webkit-sticky' && cs.position !== 'fixed') { return; }
      if (isHidden(el)) { return; }
      /* keep outermost only: drop already-taken descendants of this element */
      headers = headers.filter(function (h) { return !contains(el, h.el); });
      headers.push({ el: el, top: parseFloat(cs.top) || 0 });
    });
  }
  function measureTop(vh) {
    var rs = [];
    each(headers, function (h) {
      var r = h.el.getBoundingClientRect();
      if (r.height > 0 && r.height < vh * 0.5 && r.bottom > 0 && r.top <= h.top + 1 && !isHidden(h.el)) { rs.push(r); }
    });
    /* stack contiguously from the viewport top; floating fixed panels below a gap don't count */
    rs.sort(function (a, b) { return a.top - b.top; });
    var top = 0;
    each(rs, function (r) { if (r.top <= top + 1 && r.bottom > top) { top = r.bottom; } });
    return Math.max(0, Math.round(top));
  }

  /* ---------- static (reduced motion) end states ---------- */
  function applyStatic() {
    each(scenes, function (s) {
      var p = s.name === 'hero' ? '0' : '1';
      s.p = p; setVar(s.el, '--p', p);
      each(s.words, function (g) { lightWords(g, g.words.length); });
      each(s.steps, function (l) { setVar(l.el, '--p', '1'); each(l.lis, function (li) { li.classList.add('lit'); }); });
      if (s.track) { s.track.style.transform = ''; each(s.track.children, function (m) { setVar(m, '--in', '1'); }); }
      each(s.colours, function (c) { c.set(0); });
    });
    each(marquees, function (m) { m.el.style.transform = ''; });
    each(paras, function (p) { p.el.style.removeProperty('--v'); });
    each(all('.a2l-rv'), function (el) { el.classList.add('on'); });
  }

  /* ---------- marquee pause (hover / focus inside the strip) ---------- */
  function bindMarqueePause(el) {
    var strip = el.parentNode && el.parentNode.nodeType === 1 ? el.parentNode : el;
    if (strip.__a2lPauseBound) { return; }
    strip.__a2lPauseBound = true;
    function setM(k, v) { var m = el.__a2lM; if (m) { m[k] = v; } kick(); }
    strip.addEventListener('mouseenter', function () { setM('hover', true); });
    strip.addEventListener('mouseleave', function () { setM('hover', false); });
    strip.addEventListener('focusin', function () { setM('focus', true); });
    strip.addEventListener('focusout', function (e) { if (!e.relatedTarget || !strip.contains(e.relatedTarget)) { setM('focus', false); } });
  }

  /* ---------- scroll the window to a pinned scene's progress p ---------- */
  function pinTopPx() { return Math.max(0, cur.top) + Math.max(0, cur.nav); }
  function scrollToScene(s, p) {
    if (!s || !s.pin || reduce) { return false; }
    var total = s.el.offsetHeight - s.pin.offsetHeight;
    if (!(total > 0)) { return false; }
    var y = s.el.getBoundingClientRect().top + (w.pageYOffset || root.scrollTop || 0) - pinTopPx() + clamp(p) * total;
    y = Math.max(0, Math.round(y));
    if (Math.abs(y - (w.pageYOffset || 0)) > 1) {
      try { w.scrollTo(0, y); } catch (e) { root.scrollTop = y; }
    }
    kick();
    return true;
  }
  /* Lineup: progress at which a card sits in the middle of the viewport. */
  function cardProgress(s, card) {
    var track = s.track, vw = root.clientWidth || w.innerWidth;
    var max = Math.max(0, track.scrollWidth - vw);
    if (!(max > 0)) { return -1; }
    var base = track.getBoundingClientRect().left - s.tx;
    var left = card.offsetLeft - track.offsetLeft;
    return clamp(-(vw / 2 - (base + left + card.offsetWidth / 2)) / max);
  }
  function cardOf(track, el) {
    while (el && el.parentNode !== track) { el = el.parentNode; }
    return el && el.parentNode === track ? el : null;
  }
  function showCard(track, target) {
    var s = sceneFor(track), card = cardOf(track, target);
    if (!s || s.track !== track) { return false; }
    if (s.pin) { s.pin.scrollLeft = 0; }
    if (!card || reduce) { return false; }
    var p = cardProgress(s, card);
    return p >= 0 ? scrollToScene(s, p) : false;
  }
  function onFocusIn(e) {
    var track = closestAttr(e.target, 'data-a2l-track');
    if (!track) { return; }
    showCard(track, e.target);
    /* the browser's own focus scroll can land after focusin: undo it once more */
    w.setTimeout(function () { var s = sceneFor(track); if (s && s.pin) { s.pin.scrollLeft = 0; } }, 0);
  }
  function onBlockSelect(e) {
    var el = e.target;
    if (!el || el.nodeType !== 1) { return; }
    var track = closestAttr(el, 'data-a2l-track');
    if (track && showCard(track, el)) { return; }
    var col = closestAttr(el, 'data-a2l-colours');
    if (col && col.__a2l && el.nodeName === 'BUTTON' && el.hasAttribute('data-i')) {
      var i = Number(el.getAttribute('data-i')), c = col.__a2l;
      c.set(i);
      if (scrollToScene(sceneFor(col), (i + 0.5) / Math.max(1, c.count))) { c.scrollIdx = i; return; }
    }
    var s = sceneFor(el);
    if (s && s.name !== 'steps' && scrollToScene(s, 0.9)) { return; }
    try { el.scrollIntoView({ block: 'center' }); } catch (x) { el.scrollIntoView(); }
  }

  /* ---------- (re)scan the DOM ---------- */
  function refresh() {
    scenes = []; navs = []; bars = []; marquees = []; paras = []; clocks = []; orphanWords = [];

    each(all('[data-a2l-scene], [data-a2l-steps]'), function (el) {
      if (!el.hasAttribute('data-a2l-scene') && closestAttr(el.parentNode, 'data-a2l-scene')) { return; }
      var pin = null;
      each(el.children, function (ch) { if (!pin && ch.classList.contains('a2l-pin')) { pin = ch; } });
      scenes.push({ el: el, name: el.getAttribute('data-a2l-scene') || 'steps', pin: pin, p: '', words: [], steps: [],
        colours: [], track: null, tx: 0, models: [] });
    });
    each(all('[data-a2l-words]'), function (el) {
      var g = splitWords(el), s = sceneFor(el);
      if (s) { s.words.push(g); } else { orphanWords.push(g); lightWords(g, g.words.length); }
    });
    each(all('[data-a2l-steps]'), function (el) {
      var s = sceneFor(el), lis = [];
      each(el.children, function (ch) { if (ch.nodeName === 'LI') { lis.push(ch); } });
      if (s) { s.steps.push({ el: el, lis: lis }); }
    });
    each(all('[data-a2l-track]'), function (el) {
      var s = sceneFor(el);
      if (s && !s.track) { s.track = el; s.models = Array.prototype.slice.call(el.children); }
    });
    each(all('[data-a2l-colours]'), function (el) {
      var c = makeColours(el), s = sceneFor(el);
      if (s) { s.colours.push(c); }
    });

    each(all('[data-a2l-nav]'), function (el) { navs.push({ el: el, holder: el.parentNode, h: -1, stuck: false }); });
    each(all('.a2l-progress'), function (el) { bars.push(el); });
    each(all('[data-a2l-marquee]'), function (el) {
      if (!el.hasAttribute('data-a2l-cloned')) {
        each(Array.prototype.slice.call(el.children), function (ch) {
          var c = ch.cloneNode(true);
          c.setAttribute('data-a2l-clone', '');
          c.setAttribute('aria-hidden', 'true');
          el.appendChild(c);
        });
        el.setAttribute('data-a2l-cloned', '1');
      }
      var prevM = el.__a2lM;
      var mm = { el: el, x: el.__a2lX || 0, half: 0, dir: el.getAttribute('data-a2l-marquee') === 'reverse' ? -1 : 1,
        visible: prevM ? prevM.visible : !w.IntersectionObserver, hover: false, focus: false };
      el.__a2lM = mm;
      marquees.push(mm);
      bindMarqueePause(el);
      if (w.IntersectionObserver) {
        if (!mio) {
          mio = new w.IntersectionObserver(function (entries) {
            each(entries, function (e) { if (e.target.__a2lM) { e.target.__a2lM.visible = e.isIntersecting; } });
            kick();
          });
        }
        mio.unobserve(el); mio.observe(el);
      }
    });
    each(all('[data-a2l-speed]'), function (el) { paras.push({ el: el, speed: Number(el.getAttribute('data-a2l-speed')) || 0, v: '' }); });
    each(all('[data-a2l-clock][data-target]'), function (el) {
      var t = Date.parse(el.getAttribute('data-target'));
      if (!isNaN(t)) { clocks.push({ el: el, target: t, units: all('[data-u]', el), last: {} }); }
    });

    /* Reveal + counters */
    var rvs = all('.a2l-rv'), loose = [];
    each(all('[data-a2l-count]'), function (el) {
      var n = el, inRv = false;
      while (n && n.nodeType === 1) { if (n.classList.contains('a2l-rv')) { inRv = true; break; } n = n.parentNode; }
      if (!inRv) { loose.push(el); }
    });
    if (reduce) {
      applyStatic();
    } else if (!w.IntersectionObserver) {
      each(rvs, reveal); each(loose, reveal);
    } else {
      if (!io) {
        io = new w.IntersectionObserver(function (entries) {
          each(entries, function (e) { if (e.isIntersecting) { reveal(e.target); io.unobserve(e.target); } });
        }, { rootMargin: '0px 0px -10% 0px' });
      }
      each(rvs.concat(loose), function (el) {
        if (el.__a2lObserved || el.classList.contains('on')) { return; }
        el.__a2lObserved = true; io.observe(el);
      });
    }

    tickClocks();
    if (!clockTimer && clocks.length) { clockTimer = w.setInterval(tickClocks, 1000); }
    force = true; needMeasure = true; lastCollect = -1e9;
    kick();
  }

  /* ---------- the one rAF loop: read everything, then write ---------- */
  function frame(now) {
    now = now || Date.now();
    var dt = lastT ? Math.min(50, now - lastT) : 16.67; lastT = now;

    /* READ */
    var vh = w.innerHeight, vw = root.clientWidth || w.innerWidth;
    var y = w.pageYOffset || root.scrollTop || 0;
    if (now - lastCollect > 500) { collectHeaders(); lastCollect = now; }
    var top = measureTop(vh), navH = 0;
    each(navs, function (n) {
      n.rh = n.el.offsetHeight; n.rtop = n.holder.getBoundingClientRect().top;
      if (n.rh > navH) { navH = n.rh; }
    });
    var sh = root.scrollHeight;
    var dirty = force || y !== cur.y || vh !== cur.vh || vw !== cur.vw || top !== cur.top || navH !== cur.nav;
    var pinTop = top + navH, dy = cur.y < 0 ? 0 : y - cur.y, measured = needMeasure;
    /* Available pin height: a real pin's height (svh-based) when pinned. */
    var ph = 0;
    if (!reduce) { for (var si = 0; si < scenes.length && !ph; si++) { if (scenes[si].pin) { ph = scenes[si].pin.offsetHeight; } } }
    if (!ph) { ph = vh - pinTop; }
    ph = Math.max(0, Math.round(ph));

    if (needMeasure) {
      each(marquees, function (m) {
        var first = m.el.firstElementChild, clone = m.el.querySelector('[data-a2l-clone]');
        m.half = first && clone ? clone.offsetLeft - first.offsetLeft : m.el.scrollWidth / 2;
      });
    }
    if (dirty && !reduce) {
      each(scenes, function (s) {
        var r = s.el.getBoundingClientRect(), p;
        if (s.name === 'steps') { p = r.height > 0 ? clamp((vh * 0.75 - r.top) / r.height) : 0; }
        else {
          var pinH = s.pin ? s.pin.offsetHeight : vh - pinTop, total = s.el.offsetHeight - pinH;
          p = total > 0 ? clamp((pinTop - r.top) / total) : 0;
        }
        s.np = p;
        if (s.track) {
          s.max = Math.max(0, s.track.scrollWidth - vw);
          s.base = s.track.getBoundingClientRect().left - s.tx;
          s.offs = [];
          each(s.models, function (m) { s.offs.push(m.offsetLeft - s.track.offsetLeft); });
        }
      });
      each(paras, function (pr) { var r = pr.el.getBoundingClientRect(); pr.nv = ((r.top + r.height / 2 - vh / 2) * pr.speed).toFixed(1); });
    }

    /* WRITE */
    if (top !== cur.top) { setVar(root, '--a2l-top', top + 'px'); }
    if (navH !== cur.nav) { setVar(root, '--a2l-nav', navH + 'px'); }
    if (ph !== pinH) { pinH = ph; setVar(root, '--a2l-pin-h', ph + 'px'); }
    each(navs, function (n) {
      if (n.rh && n.rh !== n.h && !n.stuck) { n.h = n.rh; n.holder.style.minHeight = n.rh + 'px'; }
      var stuck = n.rtop <= top + 0.5 && y > 0;
      if (stuck !== n.stuck) { n.stuck = stuck; n.el.classList.toggle('stuck', stuck); }
    });
    if (dirty) {
      var page = (sh - vh > 0 ? clamp(y / (sh - vh)) : 0).toFixed(4);
      if (page !== cur.page) { each(bars, function (b) { setVar(b, '--page', page); }); cur.page = page; }
    }
    if (dirty && !reduce) {
      each(scenes, function (s) {
        var p = s.np, ps = p.toFixed(4);
        if (ps !== s.p) { s.p = ps; setVar(s.el, '--p', ps); }
        each(s.words, function (g) { lightWords(g, Math.floor(p * 1.15 * g.words.length)); });
        each(s.steps, function (l) {
          var n = l.lis.length;
          if (l.el !== s.el) { setVar(l.el, '--p', ps); }
          each(l.lis, function (li, i) { li.classList.toggle('lit', n > 1 ? p >= i / (n - 1) - 0.02 : p > 0); });
        });
        if (s.track) {
          var tx = -s.max * p;
          s.tx = tx;
          s.track.style.transform = 'translate3d(' + tx.toFixed(1) + 'px,0,0)';
          each(s.models, function (m, i) {
            var left = s.base + s.offs[i] + tx;
            setVar(m, '--in', clamp(1 - (left - vw * 0.72) / (vw * 0.4)).toFixed(3));
          });
        }
        each(s.colours, function (c) {
          var idx = Math.min(c.count - 1, Math.floor(p * c.count));
          if (idx !== c.scrollIdx) { c.scrollIdx = idx; c.set(idx); }
        });
      });
      each(paras, function (pr) { if (pr.nv !== pr.v) { pr.v = pr.nv; setVar(pr.el, '--v', pr.nv); } });
    }
    var moving = false;
    if (!reduce) {
      var k = dt / 16.67;
      each(marquees, function (m) {
        if (!(m.half > 0) || m.hover || m.focus) { return; }
        if (m.visible) { moving = true; }
        m.x -= m.dir * (k + dy * 0.35);
        while (m.x < -m.half) { m.x += m.half; }
        while (m.x > 0) { m.x -= m.half; }
        m.el.__a2lX = m.x;
        if (!m.visible) { return; }
        m.el.style.transform = 'translate3d(' + m.x.toFixed(2) + 'px,0,0)';
      });
    }
    if (counters.length) {
      counters = counters.filter(function (c) {
        if (!c.t0) { c.t0 = now; }
        var t = Math.min(1, (now - c.t0) / 1100), e = 1 - Math.pow(1 - t, 3);
        c.el.textContent = (c.to * e).toFixed(c.dec);
        return t < 1;
      });
    }

    cur.y = y; cur.vh = vh; cur.vw = vw; cur.top = top; cur.nav = navH;
    force = false; needMeasure = false;

    /* Idle: keep going while anything changed or moves; stop ~1 s after that. */
    if (kicked || dirty || measured || moving || counters.length) { lastBusy = now; kicked = false; }
    if (now - lastBusy < 1000) { raf(frame); } else { running = false; }
  }

  /* ---------- wiring ---------- */
  w.addEventListener('resize', function () { force = true; needMeasure = true; lastCollect = -1e9; kick(); }, { passive: true });
  w.addEventListener('scroll', kick, { passive: true });
  w.addEventListener('load', function () { force = true; needMeasure = true; kick(); }, { passive: true });
  /* header menus/search open on input without scrolling: re-check the stack */
  each(['pointerdown', 'keydown'], function (ev) { w.addEventListener(ev, function () { lastCollect = -1e9; kick(); }, { passive: true }); });
  if (d.fonts && d.fonts.ready && d.fonts.ready.then) { d.fonts.ready.then(function () { force = true; needMeasure = true; kick(); }); }
  d.addEventListener('focusin', onFocusIn);
  each(['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'], function (ev) {
    d.addEventListener(ev, function () { w.setTimeout(refresh, 0); });
  });
  d.addEventListener('shopify:block:select', function (e) { w.setTimeout(function () { onBlockSelect(e); }, 0); });
  function onMotionPref() {
    reduce = !!(mq && mq.matches);
    root.classList.toggle('a2l-static', reduce);
    refresh();
  }
  if (mq) { if (mq.addEventListener) { mq.addEventListener('change', onMotionPref); } else if (mq.addListener) { mq.addListener(onMotionPref); } }

  w.A2Launch = { refresh: refresh };
  if (d.readyState === 'loading') { d.addEventListener('DOMContentLoaded', refresh); } else { refresh(); }
})(window, document);
